import { useMemo, useState } from 'react';
import { api, ApiError, formatDate, formatDateTime } from '../../../lib/api';
import { useAuth } from '../../../lib/auth';
import type {
  CaseAccessLogEntry,
  CaseConferenceView,
  DvSupportCaseDetail,
  GuardiansResponse,
  CaseDocument,
  RiskLevel,
  SocialCase,
  SocialCaseDetail,
} from '../../../lib/types';
import { useApiQuery } from '../../../lib/useApi';
import { EmptyState, ErrorNote, Field, Modal, PageHeader, Spinner, StatusBadge } from '../../../ui/components';
import { DvCasePanel, openOrCreateDvSupportCase } from './DvSupport';

// Role strings verified against m27-family-affairs/src/service/caseAccess.ts.
const SENIOR_ROLES = ['SENIOR_SW'];
const SOCIAL_WORKER_ROLES = ['SOCIAL_WORKER', 'SENIOR_SW'];
const MULTI_AGENCY_ROLES = ['MULTI_AGENCY_MEMBER'];

/**
 * Case register — the central need-to-know screen. The UI picks the ONE
 * list endpoint that matches the caller's role rather than offering a
 * control that would just 403, mirroring the backend's own
 * FULL/CONFERENCE/NONE resolution
 * (m27-family-affairs/src/service/caseAccess.ts):
 *  - SENIOR_SW -> GET /cases (full supervisory browse, any case, any
 *    caseworker's caseload)
 *  - SOCIAL_WORKER -> GET /cases/mine (own assigned caseload only — there
 *    is no way for a plain caseworker to browse a colleague's cases, by
 *    design)
 *  - MULTI_AGENCY_MEMBER (with no SW/senior role) -> no list endpoint
 *    exists at all for "cases I'm a conference participant on" (flagged as
 *    a backend gap in this app's build report); they open a specific case's
 *    read-only conference (shared plan) view by ID, told to them out of
 *    band by the assigned caseworker when granted access.
 */
export function CaseRegister() {
  const { persona } = useAuth();
  const roles = persona?.roles ?? [];
  const isSenior = roles.some((r) => SENIOR_ROLES.includes(r));
  const isSocialWorker = roles.some((r) => SOCIAL_WORKER_ROLES.includes(r));
  const isMultiAgencyOnly = !isSocialWorker && roles.some((r) => MULTI_AGENCY_ROLES.includes(r));

  const [statusFilter, setStatusFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [districtFilter, setDistrictFilter] = useState('');
  const [q, setQ] = useState('');
  const [openId, setOpenId] = useState<string | null>(null);
  const [conferenceCaseId, setConferenceCaseId] = useState<string | null>(null);
  const [lookupCaseId, setLookupCaseId] = useState('');

  const seniorQuery = useMemo(() => {
    const p = new URLSearchParams({ limit: '50' });
    if (statusFilter) p.set('status', statusFilter);
    if (categoryFilter) p.set('category', categoryFilter);
    if (districtFilter) p.set('district', districtFilter);
    if (q) p.set('q', q);
    return `/api/v1/m27/cases?${p}`;
  }, [statusFilter, categoryFilter, districtFilter, q]);
  const mineQuery = useMemo(() => {
    const p = new URLSearchParams({ limit: '50' });
    if (statusFilter) p.set('status', statusFilter);
    return `/api/v1/m27/cases/mine?${p}`;
  }, [statusFilter]);

  const listQ = useApiQuery<{ items: SocialCase[]; nextCursor: string | null }>(
    isSenior ? seniorQuery : isSocialWorker ? mineQuery : null,
  );

  if (!isSocialWorker && !isMultiAgencyOnly) {
    return (
      <div>
        <PageHeader title="Case Register" />
        <ErrorNote message="This view requires the Social Worker, Senior Social Worker, or Multi-Agency Conference Member role." />
      </div>
    );
  }

  if (isMultiAgencyOnly) {
    return (
      <div>
        <PageHeader
          title="Case Register — Conference View"
          subtitle="Your role grants read-only access to the shared multi-agency plan for cases you've been added to as a participant — never the full case file"
        />
        <div className="card max-w-lg p-4">
          <div className="field-label mb-2">Open a case's conference view</div>
          <p className="mb-3 text-xs text-slate-500">
            There is no browse-all list for cases you're a conference participant on — the assigned caseworker gives you the
            case ID when they add you to the conference.
          </p>
          <div className="flex gap-2">
            <input className="field-input" value={lookupCaseId} onChange={(e) => setLookupCaseId(e.target.value)} placeholder="social case id" />
            <button className="btn-primary shrink-0" onClick={() => setConferenceCaseId(lookupCaseId)} disabled={!lookupCaseId}>
              Open
            </button>
          </div>
        </div>
        {conferenceCaseId ? <ConferenceModal caseId={conferenceCaseId} onClose={() => setConferenceCaseId(null)} /> : null}
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title="Case Register"
        subtitle={isSenior ? 'Supervisory view — every case, any caseworker' : 'Your assigned caseload only — need-to-know partition'}
      />
      <div className="mb-4 flex flex-wrap gap-3">
        <select className="field-input max-w-[160px]" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
          <option value="">All statuses</option>
          <option value="OPEN">Open</option>
          <option value="MONITORING">Monitoring</option>
          <option value="CLOSED">Closed</option>
        </select>
        {isSenior ? (
          <>
            <select className="field-input max-w-[200px]" value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)}>
              <option value="">All categories</option>
              <option value="CHILD_PROTECTION">Child protection</option>
              <option value="DV">Domestic violence</option>
              <option value="ELDERLY">Elderly</option>
              <option value="DISABILITY_SUPPORT">Disability support</option>
            </select>
            <input className="field-input max-w-xs" placeholder="Filter by district slug…" value={districtFilter} onChange={(e) => setDistrictFilter(e.target.value)} />
            <input className="field-input max-w-xs" placeholder="Search subject name or ref…" value={q} onChange={(e) => setQ(e.target.value)} />
          </>
        ) : null}
      </div>

      <div className="card overflow-x-auto">
        {listQ.loading ? (
          <Spinner />
        ) : listQ.error ? (
          <ErrorNote message={listQ.error} />
        ) : (listQ.data?.items.length ?? 0) === 0 ? (
          <EmptyState title="No cases found" />
        ) : (
          <table className="w-full">
            <thead>
              <tr>
                <th className="table-head">Ref</th>
                <th className="table-head">Subject</th>
                <th className="table-head">Category</th>
                <th className="table-head">Risk</th>
                <th className="table-head">District</th>
                <th className="table-head">Status</th>
                {isSenior ? <th className="table-head">Assigned to</th> : null}
                <th className="table-head" />
              </tr>
            </thead>
            <tbody>
              {listQ.data!.items.map((c) => (
                <tr key={c.id} className="hover:bg-slate-50">
                  <td className="table-cell font-medium">{c.refNo}</td>
                  <td className="table-cell text-xs">
                    {c.subjectNin ? c.subjectName : <span className="italic text-slate-400">{c.subjectName} (unidentified)</span>}
                  </td>
                  <td className="table-cell">
                    <StatusBadge status={c.category} />
                  </td>
                  <td className="table-cell">
                    <StatusBadge status={c.riskLevel} />
                  </td>
                  <td className="table-cell text-xs">{c.district.replaceAll('-', ' ')}</td>
                  <td className="table-cell">
                    <StatusBadge status={c.status} />
                  </td>
                  {isSenior ? <td className="table-cell text-xs">{c.assignedTo}</td> : null}
                  <td className="table-cell text-right">
                    <button className="btn-secondary !px-2.5 !py-1 text-xs" onClick={() => setOpenId(c.id)}>
                      Open
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {openId ? (
        <CaseDetailModal
          id={openId}
          isSenior={isSenior}
          onClose={() => setOpenId(null)}
          onChanged={() => listQ.reload()}
        />
      ) : null}
    </div>
  );
}

function CaseDetailModal({
  id,
  isSenior,
  onClose,
  onChanged,
}: {
  id: string;
  isSenior: boolean;
  onClose: () => void;
  onChanged: () => void;
}) {
  const caseQ = useApiQuery<SocialCaseDetail>(`/api/v1/m27/cases/${id}`);
  const documentsQ = useApiQuery<{ items: CaseDocument[] }>(`/api/v1/m27/cases/${id}/documents`);
  const guardiansQ = useApiQuery<GuardiansResponse>(`/api/v1/m27/cases/${id}/guardians`);
  const accessLogQ = useApiQuery<{ items: CaseAccessLogEntry[] }>(`/api/v1/m27/cases/${id}/access-log`);

  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [showConference, setShowConference] = useState(false);
  const [showAccessLog, setShowAccessLog] = useState(false);
  const [showDvSupport, setShowDvSupport] = useState(false);
  const [dvCase, setDvCase] = useState<DvSupportCaseDetail | null>(null);

  const refresh = () => {
    caseQ.reload();
    documentsQ.reload();
    accessLogQ.reload();
    guardiansQ.reload();
    onChanged();
  };

  async function run(action: () => Promise<void>) {
    setBusy(true);
    setError(null);
    try {
      await action();
      refresh();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Action failed');
    } finally {
      setBusy(false);
    }
  }

  // ── identify subject ──
  const [subjectNin, setSubjectNin] = useState('');

  // ── close / reopen / reassign ──
  const [showClose, setShowClose] = useState(false);
  const [closureSummary, setClosureSummary] = useState('');
  const [showReopen, setShowReopen] = useState(false);
  const [reopenReason, setReopenReason] = useState('');
  const [showReassign, setShowReassign] = useState(false);
  const [reassignTo, setReassignTo] = useState('');

  // ── interventions ──
  const [showAddIntervention, setShowAddIntervention] = useState(false);
  const [ivType, setIvType] = useState('');
  const [ivDesc, setIvDesc] = useState('');
  const [ivDate, setIvDate] = useState('');
  const [completingIv, setCompletingIv] = useState<string | null>(null);
  const [ivOutcome, setIvOutcome] = useState('');

  // ── plan items ──
  const [showAddPlan, setShowAddPlan] = useState(false);
  const [planAgency, setPlanAgency] = useState('');
  const [planAction, setPlanAction] = useState('');
  const [planOwner, setPlanOwner] = useState('');
  const [planDue, setPlanDue] = useState('');

  // ── participants ──
  const [showAddParticipant, setShowAddParticipant] = useState(false);
  const [pSubject, setPSubject] = useState('');
  const [pName, setPName] = useState('');
  const [pAgency, setPAgency] = useState('');
  const [pRole, setPRole] = useState<'CHAIR' | 'MEMBER'>('MEMBER');

  // ── court refs ──
  const [showAddCourtRef, setShowAddCourtRef] = useState(false);
  const [courtRef, setCourtRef] = useState('');
  const [orderType, setOrderType] = useState('CARE_ORDER');
  const [orderDate, setOrderDate] = useState('');
  const [orderSummary, setOrderSummary] = useState('');

  // ── documents ──
  const [showAddDocument, setShowAddDocument] = useState(false);
  const [docKind, setDocKind] = useState('ASSESSMENT');
  const [docTitle, setDocTitle] = useState('');
  const [docStorageRef, setDocStorageRef] = useState('');
  const [docSha256, setDocSha256] = useState('');

  // ── visits ──
  const [showAddVisit, setShowAddVisit] = useState(false);
  const [visitedAt, setVisitedAt] = useState(() => new Date().toISOString().slice(0, 16));
  const [visitLocation, setVisitLocation] = useState('');
  const [visitFindings, setVisitFindings] = useState('');
  const [visitRisk, setVisitRisk] = useState<RiskLevel | ''>('');
  const [visitOffline, setVisitOffline] = useState(false);

  const openDvSupport = async () => {
    setBusy(true);
    setError(null);
    try {
      const dvId = await openOrCreateDvSupportCase(id);
      const dv = await api.get<DvSupportCaseDetail>(`/api/v1/m27/dv-cases/${dvId}`);
      setDvCase(dv);
      setShowDvSupport(true);
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Could not open DV support case');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title={caseQ.data ? `Case ${caseQ.data.refNo}` : 'Case'} onClose={onClose} wide>
      {caseQ.loading ? (
        <Spinner />
      ) : caseQ.error ? (
        <ErrorNote message={caseQ.error} />
      ) : caseQ.data ? (
        <div className="space-y-5">
          {error ? <ErrorNote message={error} /> : null}

          <div>
            <div className="flex flex-wrap items-center gap-2">
              <StatusBadge status={caseQ.data.category} />
              <StatusBadge status={caseQ.data.riskLevel} />
              <StatusBadge status={caseQ.data.status} />
            </div>
            <div className="mt-2 text-lg font-bold text-slate-900">
              {caseQ.data.subjectNin ? caseQ.data.subjectName : `${caseQ.data.subjectName} (subject not yet identified)`}
            </div>
            <div className="text-xs text-slate-500">
              {caseQ.data.subjectNin ? `NIN ${caseQ.data.subjectNin} · ` : ''}
              District {caseQ.data.district.replaceAll('-', ' ')} · Assigned to {caseQ.data.assignedTo} · Opened by {caseQ.data.openedBy}
            </div>
            {caseQ.data.status === 'CLOSED' ? (
              <p className="mt-1 text-xs text-slate-500">
                Closed {formatDateTime(caseQ.data.closedAt)} by {caseQ.data.closedBy}: {caseQ.data.closureSummary}
              </p>
            ) : null}
          </div>

          {!caseQ.data.subjectNin ? (
            <div className="flex items-end gap-2 rounded-lg border border-slate-200 p-3">
              <Field label="Identify subject (NIN)" className="flex-1">
                <input className="field-input" value={subjectNin} onChange={(e) => setSubjectNin(e.target.value)} />
              </Field>
              <button
                className="btn-secondary !py-2"
                disabled={busy || !subjectNin}
                onClick={() => run(async () => { await api.post(`/api/v1/m27/cases/${id}/identify-subject`, { subjectNin }); })}
              >
                Identify
              </button>
            </div>
          ) : null}

          <div className="flex flex-wrap gap-2">
            {caseQ.data.status === 'OPEN' ? (
              <button className="btn-secondary !py-1 text-xs" disabled={busy} onClick={() => run(async () => { await api.post(`/api/v1/m27/cases/${id}/step-down`); })}>
                Step down to monitoring
              </button>
            ) : null}
            {caseQ.data.status === 'MONITORING' ? (
              <button className="btn-secondary !py-1 text-xs" disabled={busy} onClick={() => run(async () => { await api.post(`/api/v1/m27/cases/${id}/resume`); })}>
                Resume active casework
              </button>
            ) : null}
            {caseQ.data.status !== 'CLOSED' ? (
              <button className="btn-secondary !py-1 text-xs" onClick={() => setShowClose((v) => !v)}>
                Close case
              </button>
            ) : null}
            {caseQ.data.status === 'CLOSED' && isSenior ? (
              <button className="btn-secondary !py-1 text-xs" onClick={() => setShowReopen((v) => !v)}>
                Reopen case
              </button>
            ) : null}
            {isSenior ? (
              <button className="btn-secondary !py-1 text-xs" onClick={() => setShowReassign((v) => !v)}>
                Reassign
              </button>
            ) : null}
            <button className="btn-secondary !py-1 text-xs" onClick={() => setShowConference(true)}>
              Preview conference view
            </button>
            <button className="btn-secondary !py-1 text-xs" onClick={() => setShowAccessLog((v) => !v)}>
              {showAccessLog ? 'Hide' : 'Show'} access log
            </button>
            {caseQ.data.category === 'DV' ? (
              <button className="btn-secondary !py-1 text-xs" disabled={busy} onClick={openDvSupport}>
                Open DV support case
              </button>
            ) : null}
          </div>

          {showClose ? (
            <div className="rounded-lg border border-slate-200 p-3">
              <Field label="Closure summary">
                <textarea className="field-input" rows={2} value={closureSummary} onChange={(e) => setClosureSummary(e.target.value)} />
              </Field>
              <div className="mt-2 flex justify-end">
                <button
                  className="btn-primary !py-1 text-xs"
                  disabled={busy || closureSummary.length < 10}
                  onClick={() => run(async () => { await api.post(`/api/v1/m27/cases/${id}/close`, { closureSummary }); setShowClose(false); })}
                >
                  Confirm close
                </button>
              </div>
            </div>
          ) : null}
          {showReopen ? (
            <div className="rounded-lg border border-slate-200 p-3">
              <Field label="Reopen reason">
                <textarea className="field-input" rows={2} value={reopenReason} onChange={(e) => setReopenReason(e.target.value)} />
              </Field>
              <div className="mt-2 flex justify-end">
                <button
                  className="btn-primary !py-1 text-xs"
                  disabled={busy || reopenReason.length < 10}
                  onClick={() => run(async () => { await api.post(`/api/v1/m27/cases/${id}/reopen`, { reopenReason }); setShowReopen(false); })}
                >
                  Confirm reopen
                </button>
              </div>
            </div>
          ) : null}
          {showReassign ? (
            <div className="flex items-end gap-2 rounded-lg border border-slate-200 p-3">
              <Field label="Reassign to (caseworker subject id)" className="flex-1">
                <input className="field-input" value={reassignTo} onChange={(e) => setReassignTo(e.target.value)} placeholder="acc_sw_kelly_dubignon" />
              </Field>
              <button
                className="btn-primary !py-2"
                disabled={busy || !reassignTo}
                onClick={() => run(async () => { await api.post(`/api/v1/m27/cases/${id}/reassign`, { assignTo: reassignTo }); setShowReassign(false); })}
              >
                Reassign
              </button>
            </div>
          ) : null}

          {showAccessLog ? (
            <div>
              <h3 className="mb-1 text-sm font-bold text-slate-700">Access log</h3>
              {accessLogQ.loading ? (
                <Spinner />
              ) : accessLogQ.error ? (
                <ErrorNote message={accessLogQ.error} />
              ) : (
                <ul className="max-h-40 space-y-1 overflow-y-auto text-xs text-slate-500">
                  {(accessLogQ.data?.items ?? []).map((l) => (
                    <li key={l.id}>
                      {formatDateTime(l.createdAt)} — {l.actorSubject} ({l.actorRoles.join(', ')}): {l.action}
                      {l.context ? ` [${l.context}]` : ''}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ) : null}

          <div>
            <div className="mb-1 flex items-center justify-between">
              <h3 className="text-sm font-bold text-slate-700">Interventions</h3>
              <button className="btn-secondary !px-2 !py-0.5 text-xs" onClick={() => setShowAddIntervention((v) => !v)}>
                + Add
              </button>
            </div>
            {caseQ.data.interventions.length === 0 ? (
              <EmptyState title="No interventions recorded" />
            ) : (
              <ul className="space-y-1.5">
                {caseQ.data.interventions.map((iv) => (
                  <li key={iv.id} className="rounded-lg border border-slate-200 p-2.5 text-sm">
                    <div className="flex items-center justify-between">
                      <span className="font-medium">{iv.type}</span>
                      <StatusBadge status={iv.status} />
                    </div>
                    <p className="text-xs text-slate-600">{iv.description}</p>
                    {iv.outcome ? <p className="mt-1 text-xs text-slate-500">Outcome: {iv.outcome}</p> : null}
                    {(iv.status === 'PLANNED' || iv.status === 'IN_PROGRESS') ? (
                      completingIv === iv.id ? (
                        <div className="mt-1.5 flex items-center gap-1.5">
                          <input className="field-input !py-1 text-xs" value={ivOutcome} onChange={(e) => setIvOutcome(e.target.value)} placeholder="Outcome" />
                          <button
                            className="btn-primary !px-2 !py-0.5 text-xs"
                            disabled={busy || ivOutcome.length < 3}
                            onClick={() => run(async () => { await api.post(`/api/v1/m27/cases/${id}/interventions/${iv.id}/complete`, { outcome: ivOutcome }); setCompletingIv(null); setIvOutcome(''); })}
                          >
                            Save
                          </button>
                        </div>
                      ) : (
                        <button className="btn-secondary mt-1.5 !px-2 !py-0.5 text-xs" onClick={() => setCompletingIv(iv.id)}>
                          Mark complete
                        </button>
                      )
                    ) : null}
                  </li>
                ))}
              </ul>
            )}
            {showAddIntervention ? (
              <div className="mt-2 space-y-2 rounded-lg border border-slate-200 p-3">
                <div className="grid grid-cols-2 gap-2">
                  <Field label="Type">
                    <input className="field-input" value={ivType} onChange={(e) => setIvType(e.target.value)} placeholder="HOME_VISIT" />
                  </Field>
                  <Field label="Planned date">
                    <input className="field-input" type="date" value={ivDate} onChange={(e) => setIvDate(e.target.value)} />
                  </Field>
                </div>
                <Field label="Description">
                  <textarea className="field-input" rows={2} value={ivDesc} onChange={(e) => setIvDesc(e.target.value)} />
                </Field>
                <div className="flex justify-end">
                  <button
                    className="btn-primary !py-1 text-xs"
                    disabled={busy || !ivType || ivDesc.length < 3}
                    onClick={() => run(async () => {
                      await api.post(`/api/v1/m27/cases/${id}/interventions`, { type: ivType, description: ivDesc, plannedDate: ivDate || undefined });
                      setIvType(''); setIvDesc(''); setIvDate(''); setShowAddIntervention(false);
                    })}
                  >
                    Add intervention
                  </button>
                </div>
              </div>
            ) : null}
          </div>

          <div>
            <div className="mb-1 flex items-center justify-between">
              <h3 className="text-sm font-bold text-slate-700">Multi-agency plan</h3>
              <button className="btn-secondary !px-2 !py-0.5 text-xs" onClick={() => setShowAddPlan((v) => !v)}>
                + Add
              </button>
            </div>
            {caseQ.data.planItems.length === 0 ? (
              <EmptyState title="No plan items" />
            ) : (
              <ul className="space-y-1.5">
                {caseQ.data.planItems.map((p) => (
                  <li key={p.id} className="rounded-lg border border-slate-200 p-2.5 text-sm">
                    <div className="flex items-center justify-between">
                      <span className="font-medium">{p.agency.replaceAll('_', ' ')}</span>
                      <select
                        className="field-input max-w-[130px] !py-0.5 text-xs"
                        value={p.status}
                        disabled={busy}
                        onChange={(e) => run(async () => { await api.post(`/api/v1/m27/cases/${id}/plan-items/${p.id}/status`, { status: e.target.value }); })}
                      >
                        <option value="PENDING">Pending</option>
                        <option value="IN_PROGRESS">In progress</option>
                        <option value="DONE">Done</option>
                      </select>
                    </div>
                    <p className="text-xs text-slate-600">{p.action}</p>
                    <p className="text-xs text-slate-400">Owner: {p.owner}{p.dueDate ? ` · due ${formatDate(p.dueDate)}` : ''}</p>
                  </li>
                ))}
              </ul>
            )}
            {showAddPlan ? (
              <div className="mt-2 space-y-2 rounded-lg border border-slate-200 p-3">
                <div className="grid grid-cols-2 gap-2">
                  <Field label="Agency">
                    <input className="field-input" value={planAgency} onChange={(e) => setPlanAgency(e.target.value)} placeholder="M28_EDUCATION" />
                  </Field>
                  <Field label="Owner">
                    <input className="field-input" value={planOwner} onChange={(e) => setPlanOwner(e.target.value)} />
                  </Field>
                </div>
                <Field label="Action">
                  <input className="field-input" value={planAction} onChange={(e) => setPlanAction(e.target.value)} />
                </Field>
                <Field label="Due date">
                  <input className="field-input" type="date" value={planDue} onChange={(e) => setPlanDue(e.target.value)} />
                </Field>
                <div className="flex justify-end">
                  <button
                    className="btn-primary !py-1 text-xs"
                    disabled={busy || !planAgency || !planAction || !planOwner}
                    onClick={() => run(async () => {
                      await api.post(`/api/v1/m27/cases/${id}/plan-items`, { agency: planAgency, action: planAction, owner: planOwner, dueDate: planDue || undefined });
                      setPlanAgency(''); setPlanAction(''); setPlanOwner(''); setPlanDue(''); setShowAddPlan(false);
                    })}
                  >
                    Add plan item
                  </button>
                </div>
              </div>
            ) : null}
          </div>

          <div>
            <div className="mb-1 flex items-center justify-between">
              <h3 className="text-sm font-bold text-slate-700">Conference participants (need-to-know grants)</h3>
              <button className="btn-secondary !px-2 !py-0.5 text-xs" onClick={() => setShowAddParticipant((v) => !v)}>
                + Grant access
              </button>
            </div>
            {caseQ.data.participants.length === 0 ? (
              <EmptyState title="No conference participants granted" />
            ) : (
              <ul className="space-y-1.5">
                {caseQ.data.participants.map((p) => (
                  <li key={p.id} className="flex items-center justify-between rounded-lg border border-slate-200 p-2.5 text-sm">
                    <div>
                      <span className="font-medium">{p.name}</span> — {p.agency} ({p.role})
                      {p.removedAt ? <span className="ml-2 text-xs text-slate-400">revoked {formatDate(p.removedAt)}</span> : null}
                    </div>
                    {!p.removedAt ? (
                      <button
                        className="btn-secondary !px-2 !py-0.5 text-xs"
                        disabled={busy}
                        onClick={() => run(async () => { await api.post(`/api/v1/m27/cases/${id}/participants/${p.id}/remove`); })}
                      >
                        Revoke
                      </button>
                    ) : null}
                  </li>
                ))}
              </ul>
            )}
            {showAddParticipant ? (
              <div className="mt-2 space-y-2 rounded-lg border border-slate-200 p-3">
                <div className="grid grid-cols-2 gap-2">
                  <Field label="Subject id (SeyID)">
                    <input className="field-input" value={pSubject} onChange={(e) => setPSubject(e.target.value)} placeholder="acc_m28_counsellor_adeline_green" />
                  </Field>
                  <Field label="Display name">
                    <input className="field-input" value={pName} onChange={(e) => setPName(e.target.value)} />
                  </Field>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <Field label="Agency">
                    <input className="field-input" value={pAgency} onChange={(e) => setPAgency(e.target.value)} placeholder="M28_EDUCATION" />
                  </Field>
                  <Field label="Role">
                    <select className="field-input" value={pRole} onChange={(e) => setPRole(e.target.value as 'CHAIR' | 'MEMBER')}>
                      <option value="MEMBER">Member</option>
                      <option value="CHAIR">Chair</option>
                    </select>
                  </Field>
                </div>
                <div className="flex justify-end">
                  <button
                    className="btn-primary !py-1 text-xs"
                    disabled={busy || !pSubject || !pName || !pAgency}
                    onClick={() => run(async () => {
                      await api.post(`/api/v1/m27/cases/${id}/participants`, { subject: pSubject, name: pName, agency: pAgency, role: pRole });
                      setPSubject(''); setPName(''); setPAgency(''); setShowAddParticipant(false);
                    })}
                  >
                    Grant conference access
                  </button>
                </div>
              </div>
            ) : null}
          </div>

          <div>
            <div className="mb-1 flex items-center justify-between">
              <h3 className="text-sm font-bold text-slate-700">Court references (M37 sealed channel)</h3>
              <button className="btn-secondary !px-2 !py-0.5 text-xs" onClick={() => setShowAddCourtRef((v) => !v)}>
                + Add
              </button>
            </div>
            {caseQ.data.courtRefs.length === 0 ? (
              <EmptyState title="No court references on file" />
            ) : (
              <ul className="space-y-1.5">
                {caseQ.data.courtRefs.map((c) => (
                  <li key={c.id} className="rounded-lg border border-slate-200 p-2.5 text-sm">
                    <div className="font-medium">{c.orderType.replaceAll('_', ' ')} — {c.courtCaseRef}</div>
                    {c.orderDate ? <div className="text-xs text-slate-400">{formatDate(c.orderDate)}</div> : null}
                    {c.summary ? <p className="text-xs text-slate-600">{c.summary}</p> : null}
                  </li>
                ))}
              </ul>
            )}
            {showAddCourtRef ? (
              <div className="mt-2 space-y-2 rounded-lg border border-slate-200 p-3">
                <div className="grid grid-cols-2 gap-2">
                  <Field label="Court case ref">
                    <input className="field-input" value={courtRef} onChange={(e) => setCourtRef(e.target.value)} placeholder="M37-FAM-2026-00042" />
                  </Field>
                  <Field label="Order type">
                    <select className="field-input" value={orderType} onChange={(e) => setOrderType(e.target.value)}>
                      <option value="CARE_ORDER">Care order</option>
                      <option value="SUPERVISION_ORDER">Supervision order</option>
                      <option value="PROTECTION_ORDER">Protection order</option>
                      <option value="ADOPTION_ORDER">Adoption order</option>
                      <option value="OTHER">Other</option>
                    </select>
                  </Field>
                </div>
                <Field label="Order date">
                  <input className="field-input" type="date" value={orderDate} onChange={(e) => setOrderDate(e.target.value)} />
                </Field>
                <Field label="Summary">
                  <textarea className="field-input" rows={2} value={orderSummary} onChange={(e) => setOrderSummary(e.target.value)} />
                </Field>
                <div className="flex justify-end">
                  <button
                    className="btn-primary !py-1 text-xs"
                    disabled={busy || !courtRef}
                    onClick={() => run(async () => {
                      await api.post(`/api/v1/m27/cases/${id}/court-refs`, { courtCaseRef: courtRef, orderType, orderDate: orderDate || undefined, summary: orderSummary || undefined });
                      setCourtRef(''); setOrderSummary(''); setShowAddCourtRef(false);
                    })}
                  >
                    Add court reference
                  </button>
                </div>
              </div>
            ) : null}
          </div>

          <div>
            <div className="mb-1 flex items-center justify-between">
              <h3 className="text-sm font-bold text-slate-700">Internal documents</h3>
              <button className="btn-secondary !px-2 !py-0.5 text-xs" onClick={() => setShowAddDocument((v) => !v)}>
                + Add
              </button>
            </div>
            {documentsQ.loading ? (
              <Spinner />
            ) : (documentsQ.data?.items.length ?? 0) === 0 ? (
              <EmptyState title="No documents on file" />
            ) : (
              <ul className="space-y-1.5">
                {documentsQ.data!.items.map((d) => (
                  <li key={d.id} className="rounded-lg border border-slate-200 p-2.5 text-sm">
                    <div className="flex items-center justify-between">
                      <span className="font-medium">{d.title}</span>
                      <StatusBadge status={d.kind} />
                    </div>
                    <div className="text-xs text-slate-400">sha256 {d.sha256.slice(0, 12)}… · uploaded by {d.uploadedBy}</div>
                  </li>
                ))}
              </ul>
            )}
            {showAddDocument ? (
              <div className="mt-2 space-y-2 rounded-lg border border-slate-200 p-3">
                <div className="grid grid-cols-2 gap-2">
                  <Field label="Kind">
                    <select className="field-input" value={docKind} onChange={(e) => setDocKind(e.target.value)}>
                      <option value="ASSESSMENT">Assessment</option>
                      <option value="COURT_ORDER">Court order</option>
                      <option value="SAFETY_PLAN">Safety plan</option>
                      <option value="CORRESPONDENCE">Correspondence</option>
                      <option value="PHOTO_EVIDENCE">Photo evidence</option>
                      <option value="OTHER">Other</option>
                    </select>
                  </Field>
                  <Field label="Title">
                    <input className="field-input" value={docTitle} onChange={(e) => setDocTitle(e.target.value)} />
                  </Field>
                </div>
                <Field label="Storage reference (object-store key/URL)">
                  <input className="field-input" value={docStorageRef} onChange={(e) => setDocStorageRef(e.target.value)} placeholder="objstore://m27-case-docs/…" />
                </Field>
                <Field label="SHA-256 (64 hex chars)">
                  <input className="field-input font-mono" value={docSha256} onChange={(e) => setDocSha256(e.target.value)} />
                </Field>
                <div className="flex justify-end">
                  <button
                    className="btn-primary !py-1 text-xs"
                    disabled={busy || !docTitle || !docStorageRef || !/^[a-f0-9]{64}$/i.test(docSha256)}
                    onClick={() => run(async () => {
                      await api.post(`/api/v1/m27/cases/${id}/documents`, { kind: docKind, title: docTitle, storageRef: docStorageRef, sha256: docSha256 });
                      setDocTitle(''); setDocStorageRef(''); setDocSha256(''); setShowAddDocument(false);
                    })}
                  >
                    Add document
                  </button>
                </div>
              </div>
            ) : null}
          </div>

          <div>
            <div className="mb-1 flex items-center justify-between">
              <h3 className="text-sm font-bold text-slate-700">Caseworker visits</h3>
              <button className="btn-secondary !px-2 !py-0.5 text-xs" onClick={() => setShowAddVisit((v) => !v)}>
                + Record
              </button>
            </div>
            {caseQ.data.visits.length === 0 ? (
              <EmptyState title="No visits recorded" />
            ) : (
              <ul className="space-y-1.5">
                {caseQ.data.visits.map((v) => (
                  <li key={v.id} className="rounded-lg border border-slate-200 p-2.5 text-sm">
                    <div className="flex items-center justify-between">
                      <span className="font-medium">{formatDateTime(v.visitedAt)}{v.location ? ` — ${v.location}` : ''}</span>
                      {v.riskLevelAtVisit ? <StatusBadge status={v.riskLevelAtVisit} /> : null}
                    </div>
                    <p className="text-xs text-slate-600">{v.findings}</p>
                    {v.recordedOffline ? <p className="text-xs text-slate-400">Recorded offline, synced {formatDateTime(v.syncedAt)}</p> : null}
                  </li>
                ))}
              </ul>
            )}
            {showAddVisit ? (
              <div className="mt-2 space-y-2 rounded-lg border border-slate-200 p-3">
                <div className="grid grid-cols-2 gap-2">
                  <Field label="Visited at">
                    <input className="field-input" type="datetime-local" value={visitedAt} onChange={(e) => setVisitedAt(e.target.value)} />
                  </Field>
                  <Field label="Location">
                    <input className="field-input" value={visitLocation} onChange={(e) => setVisitLocation(e.target.value)} />
                  </Field>
                </div>
                <Field label="Findings">
                  <textarea className="field-input" rows={2} value={visitFindings} onChange={(e) => setVisitFindings(e.target.value)} />
                </Field>
                <div className="grid grid-cols-2 gap-2">
                  <Field label="Risk level observed">
                    <select className="field-input" value={visitRisk} onChange={(e) => setVisitRisk(e.target.value as RiskLevel | '')}>
                      <option value="">Not recorded</option>
                      <option value="LOW">Low</option>
                      <option value="MEDIUM">Medium</option>
                      <option value="HIGH">High</option>
                      <option value="CRITICAL">Critical</option>
                    </select>
                  </Field>
                  <label className="mt-6 flex items-center gap-2 text-xs font-medium text-slate-600">
                    <input type="checkbox" checked={visitOffline} onChange={(e) => setVisitOffline(e.target.checked)} />
                    Recorded offline (backdated)
                  </label>
                </div>
                <div className="flex justify-end">
                  <button
                    className="btn-primary !py-1 text-xs"
                    disabled={busy || visitFindings.length < 3}
                    onClick={() => run(async () => {
                      await api.post(`/api/v1/m27/cases/${id}/visits`, {
                        visitedAt: new Date(visitedAt).toISOString(),
                        location: visitLocation || undefined,
                        findings: visitFindings,
                        riskLevelAtVisit: visitRisk || undefined,
                        recordedOffline: visitOffline,
                      });
                      setVisitLocation(''); setVisitFindings(''); setVisitRisk(''); setVisitOffline(false); setShowAddVisit(false);
                    })}
                  >
                    Record visit
                  </button>
                </div>
              </div>
            ) : null}
          </div>

          <div>
            <h3 className="mb-1 text-sm font-bold text-slate-700">Guardians (M01 population register, best-effort)</h3>
            {guardiansQ.loading ? (
              <Spinner />
            ) : guardiansQ.error ? (
              <ErrorNote message={guardiansQ.error} />
            ) : guardiansQ.data && !guardiansQ.data.resolved ? (
              <p className="text-xs text-slate-500">{guardiansQ.data.reason ?? 'Not resolved.'}</p>
            ) : (guardiansQ.data?.items.length ?? 0) === 0 ? (
              <p className="text-xs text-slate-500">No parent/guardian links found in the population register.</p>
            ) : (
              <ul className="text-xs text-slate-600">
                {guardiansQ.data!.items.map((g, i) => (
                  <li key={i}>
                    {g.relatedNin} ({g.source})
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      ) : null}

      {showConference ? <ConferenceModal caseId={id} onClose={() => setShowConference(false)} /> : null}
      {showDvSupport && dvCase ? (
        <div className="fixed inset-0 z-[60] flex items-start justify-center overflow-y-auto bg-ink-950/50 p-4 pt-[8vh]" onClick={() => setShowDvSupport(false)}>
          <div className="card w-full max-w-2xl p-5" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="text-base font-bold text-slate-900">DV support — {dvCase.refNo}</h2>
              <button className="rounded-md px-2 py-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600" onClick={() => setShowDvSupport(false)} aria-label="Close">
                ✕
              </button>
            </div>
            <DvCasePanel
              dvCase={dvCase}
              onReload={async () => {
                const fresh = await api.get<DvSupportCaseDetail>(`/api/v1/m27/dv-cases/${dvCase.id}`);
                setDvCase(fresh);
              }}
            />
          </div>
        </div>
      ) : null}
    </Modal>
  );
}

/** GET /cases/:id/conference — FULL or CONFERENCE access, shared plan view only. */
function ConferenceModal({ caseId, onClose }: { caseId: string; onClose: () => void }) {
  const conferenceQ = useApiQuery<CaseConferenceView>(`/api/v1/m27/cases/${caseId}/conference`);
  return (
    <div className="fixed inset-0 z-[60] flex items-start justify-center overflow-y-auto bg-ink-950/50 p-4 pt-[8vh]" onClick={onClose}>
      <div className="card w-full max-w-xl p-5" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-base font-bold text-slate-900">Conference (shared plan) view</h2>
          <button className="rounded-md px-2 py-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600" onClick={onClose} aria-label="Close">
            ✕
          </button>
        </div>
        {conferenceQ.loading ? (
          <Spinner />
        ) : conferenceQ.error ? (
          <ErrorNote message={conferenceQ.error} />
        ) : conferenceQ.data ? (
          <div className="space-y-4">
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-semibold">{conferenceQ.data.refNo}</span>
              <StatusBadge status={conferenceQ.data.category} />
              <StatusBadge status={conferenceQ.data.riskLevel} />
              <StatusBadge status={conferenceQ.data.status} />
            </div>
            <p className="text-xs text-slate-500">
              This is the shared multi-agency plan — no subject identity, narrative, or intervention detail is included, by
              design.
            </p>
            <div>
              <div className="field-label">Plan items</div>
              {conferenceQ.data.planItems.length === 0 ? (
                <EmptyState title="No plan items" />
              ) : (
                <ul className="space-y-1.5">
                  {conferenceQ.data.planItems.map((p) => (
                    <li key={p.id} className="rounded-lg border border-slate-200 p-2.5 text-sm">
                      <div className="flex items-center justify-between">
                        <span className="font-medium">{p.agency.replaceAll('_', ' ')}</span>
                        <StatusBadge status={p.status} />
                      </div>
                      <p className="text-xs text-slate-600">{p.action}</p>
                      <p className="text-xs text-slate-400">Owner: {p.owner}</p>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div>
              <div className="field-label">Participants</div>
              <ul className="text-xs text-slate-600">
                {conferenceQ.data.participants.map((p) => (
                  <li key={p.id}>
                    {p.name} — {p.agency} ({p.role})
                  </li>
                ))}
              </ul>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}
